import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';

// ============================================================
// Geopolitical Intelligence Center - Country Risk View
// Restructured: tabs removed, Country Risk is the default/only view
// ============================================================

interface CountrySummary {
  country: string;
  product_count: number;
  active_recall_count: number;
  shortage_count: number;
  high_confidence_pct: number | null;
}

type SortOption = 'concentration' | 'risk' | 'country';

interface GeopoliticalIntelligencePageProps {
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconSpinner: React.ComponentType;
  IconAlert: React.ComponentType;
  IconGlobe: React.ComponentType;
  IconFlag: React.ComponentType;
  IconShield: React.ComponentType;
}

export default function GeopoliticalIntelligencePage({
  StatCard,
  IconSpinner,
  IconAlert,
  IconGlobe,
  IconFlag,
  IconShield,
}: GeopoliticalIntelligencePageProps) {
  const { T } = useTheme();
  const navigate = useNavigate();
  const { filters: globalFilters } = useGlobalFilters();

  // Build global filter query string
  const globalFilterQS = (() => {
    const p = new URLSearchParams();
    if (globalFilters.cooConfidence.length > 0) p.set('coo_confidence', globalFilters.cooConfidence.join(','));
    if (globalFilters.country.length > 0) p.set('country', globalFilters.country.join(','));
    return p.toString();
  })();

  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('concentration');
  const [tableSort, setTableSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'product_count', dir: 'desc' });
  const [countrySearch, setCountrySearch] = useState('');
  const [aggregates, setAggregates] = useState<{
    total_countries: number;
    total_products: number;
    total_products_not_covered: number;
    total_active_recalls: number;
    total_shortages: number;
    shortage_product_count: number;
  }>({
    total_countries: 0, total_products: 0, total_products_not_covered: 0,
    total_active_recalls: 0, total_shortages: 0, shortage_product_count: 0,
  });

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;

  const fetchCountries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500', offset: '0' });
      // Apply global filters
      if (globalFilters.cooConfidence.length > 0) params.set('coo_confidence', globalFilters.cooConfidence.join(','));
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      const res = await fetch(`${API_BASE}/metasupply/countries?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCountries(data.countries || []);
      setTotal(data.total || 0);
      if (data.aggregates) setAggregates(data.aggregates);
    } catch {
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, [globalFilterQS]);

  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  // Countries where confidence is meaningless — show "Unknown" instead of a %
  const isUnknownCountry = (country: string) =>
    country === 'Non-US / Unknown' || country === 'No Country Assigned';

  const getRiskLevel = (c: CountrySummary): 'critical' | 'high' | 'medium' | 'low' => {
    if (c.active_recall_count > 0 && c.shortage_count > 0) return 'critical';
    if (c.active_recall_count > 0) return 'high';
    if (c.shortage_count > 0) return 'medium';
    return 'low';
  };

  const getRiskLevelLabel = (level: string): string => {
    switch (level) {
      case 'critical': return 'Critical';
      case 'high': return 'High';
      case 'medium': return 'Medium';
      case 'low': return 'Low';
      default: return level;
    }
  };

  const getRiskLevelStyle = (level: string): { bg: string; color: string } => {
    switch (level) {
      case 'critical': return { bg: T.dangerBg, color: T.danger };
      case 'high': return { bg: T.dangerBg, color: T.danger };
      case 'medium': return { bg: T.warningBg, color: T.warning };
      case 'low': return { bg: T.successBg, color: T.success };
      default: return { bg: '#F1F5F9', color: T.textMid };
    }
  };

  const riskOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

  const filteredCountries = countries.filter(c => {
    if (!countrySearch.trim()) return true;
    return c.country.toLowerCase().includes(countrySearch.toLowerCase());
  });

  const sortedCountries = [...filteredCountries].sort((a, b) => {
    let cmp = 0;
    switch (tableSort.key) {
      case 'country': cmp = a.country.localeCompare(b.country); break;
      case 'product_count': cmp = a.product_count - b.product_count; break;
      case 'high_confidence_pct': cmp = (a.high_confidence_pct ?? -1) - (b.high_confidence_pct ?? -1); break;
      case 'risk': {
        const aR = getRiskLevel(a); const bR = getRiskLevel(b);
        cmp = riskOrder[aR] - riskOrder[bR];
        break;
      }
      case 'active_recall_count': cmp = a.active_recall_count - b.active_recall_count; break;
      case 'shortage_count': cmp = a.shortage_count - b.shortage_count; break;
      default: cmp = b.product_count - a.product_count;
    }
    return tableSort.dir === 'desc' ? -cmp : cmp;
  });

  const handleCountryClick = (country: string) => {
    if (country === 'No Country Assigned') {
      navigate('/products?coo=__no_country__');
    } else {
      navigate(`/products?coo=${encodeURIComponent(country)}`);
    }
  };

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: 'concentration', label: 'Risk Concentration' },
    { value: 'risk', label: 'Risk Level' },
    { value: 'country', label: 'Country' },
  ];

  const thStyle: React.CSSProperties = {
    padding: '12px 16px', fontSize: 11, fontWeight: 700, color: T.textLight,
    textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'left',
    borderBottom: `2px solid ${T.border}`, whiteSpace: 'nowrap', userSelect: 'none',
  };

  const tdStyle: React.CSSProperties = {
    padding: '14px 16px', fontSize: 14, borderBottom: `1px solid ${T.borderLight}`,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Hero Section */}
      <div style={{
        position: 'relative',
        background: T.offWhite, border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: isMobile ? '28px 20px' : '40px 44px',
        overflow: 'hidden', marginBottom: 24,
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <IconGlobe />
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.red,
              textTransform: 'uppercase', letterSpacing: '0.15em',
              fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
            }}>
              Global Intelligence Center
            </span>
          </div>
          <h1 style={{
            fontSize: isMobile ? 24 : 32, fontWeight: 800,
            color: T.textDark, letterSpacing: '-0.02em',
            lineHeight: 1.2, marginBottom: 8,
            fontFamily: "'Playfair Display', Georgia, serif",
          }}>
            Country Risk Assessment
          </h1>
          <p style={{
            fontSize: 14, color: T.textMid,
            maxWidth: 600, lineHeight: 1.6,
          }}>
            Analyze country-level supply chain risk across your pharmaceutical portfolio.
            View product concentration, risk levels, and supply chain alerts by country of origin.
          </p>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
        gap: isMobile ? 10 : 14, marginBottom: 24,
      }}>
        <StatCard title="Total Countries" value={aggregates.total_countries.toLocaleString()} subtitle="Sourcing origins" color={T.navy} accent="#000000" icon={<IconGlobe />} />
        <StatCard title="Drugs Linked" value={aggregates.total_products.toLocaleString()} subtitle="Across all countries" color={T.navy} accent="#000000" icon={<IconFlag />} />
        <StatCard title="Not Linked" value={aggregates.total_products_not_covered.toLocaleString()} subtitle="Unknown origin" color={T.textMid} accent="#000000" icon={<IconShield />} />
        <StatCard title="Active Recalls" value={aggregates.total_active_recalls.toLocaleString()} subtitle="Require attention" color={T.danger} accent="#000000" icon={<IconAlert />} />
        <StatCard title="Active Shortages" value={aggregates.shortage_product_count.toLocaleString()} subtitle="Unique drugs in active shortage" color={T.warning} accent="#000000" icon={<IconAlert />} />
      </div>

      {/* Sort Options + Country Table */}
      <div style={{
        background: T.white, border: `1px solid ${T.border}`,
        borderRadius: T.radius, boxShadow: T.shadow, overflow: 'hidden',
      }}>
        <div style={{
          padding: isMobile ? '16px 16px 0' : '20px 24px 0',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: 12,
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.textLight,
              textTransform: 'uppercase', letterSpacing: '0.08em',
            }}>Sort by</span>
            {sortOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                style={{
                  padding: '6px 14px', borderRadius: 20,
                  border: `1px solid ${sortBy === opt.value ? T.gold : T.border}`,
                  background: sortBy === opt.value ? `${T.gold}15` : T.white,
                  color: sortBy === opt.value ? T.navy : T.textMid,
                  fontSize: 12, fontWeight: sortBy === opt.value ? 700 : 500,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s',
                }}>
                {opt.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: T.textLight, fontWeight: 600 }}>
            {sortedCountries.length} countr{sortedCountries.length !== 1 ? 'ies' : 'y'}
          </span>
        </div>
        <div style={{ padding: isMobile ? '12px 16px' : '12px 24px' }}>
          <input
            type="text"
            placeholder="Search countries..."
            value={countrySearch}
            onChange={e => setCountrySearch(e.target.value)}
            style={{
              width: '100%', maxWidth: 320, padding: '8px 14px',
              borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
              fontSize: 13, color: T.textDark, outline: 'none',
              background: T.offWhite,
            }}
          />
        </div>        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60, color: T.textLight }}>
            <IconSpinner />
          </div>
        ) : sortedCountries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: T.textLight, fontSize: 16 }}>
            No country data available.
          </div>
        ) : isMobile ? (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sortedCountries.map(c => {
              const riskLevel = getRiskLevel(c);
              const riskStyle = getRiskLevelStyle(riskLevel);
              return (
                <div
                  key={c.country}
                  onClick={() => handleCountryClick(c.country)}
                  style={{
                    background: T.white,
                    border: `1px solid ${c.active_recall_count > 0 ? T.danger + '60' : T.border}`,
                    borderRadius: T.radiusSm, padding: 16,
                    cursor: 'pointer', transition: 'box-shadow 0.2s',
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark }}>{c.country}</div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>{c.product_count.toLocaleString()}</span>
                      <div style={{ fontSize: 10, color: T.textLight, fontWeight: 600 }}>Products</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMid, marginBottom: 4 }}>
                    High Confidence: {isUnknownCountry(c.country) ? (
                      <span style={{ fontWeight: 700, color: T.textLight }}>Unknown</span>
                    ) : c.high_confidence_pct != null ? (
                      <span style={{
                        fontWeight: 700,
                        color: c.high_confidence_pct > 75 ? T.success : c.high_confidence_pct > 50 ? T.warning : T.danger,
                      }}>{c.high_confidence_pct}%</span>
                    ) : (
                      <span style={{ fontWeight: 700, color: T.textLight }}>N/A</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                      background: riskStyle.bg, color: riskStyle.color,
                    }}>{getRiskLevelLabel(riskLevel)}</span>
                    {c.active_recall_count > 0 && (
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: T.dangerBg, color: T.danger,
                      }}>{c.active_recall_count} Recall{c.active_recall_count !== 1 ? 's' : ''}</span>
                    )}
                    {c.shortage_count > 0 && (
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: T.warningBg, color: T.warning,
                      }}>{c.shortage_count} Shortage{c.shortage_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', padding: '16px 0 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {([['Country', 'country', 'left'], ['Products', 'product_count', 'center'], ['High Confidence %', 'high_confidence_pct', 'center'], ['Risk Level', 'risk', 'center'], ['Active Recalls', 'active_recall_count', 'center'], ['Shortages', 'shortage_count', 'center']] as const).map(([label, key, align]) => (
                    <th key={key} onClick={() => {
                      if (tableSort.key === key) setTableSort(s => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }));
                      else setTableSort({ key, dir: key === 'country' ? 'asc' : 'desc' });
                    }} style={{
                      ...thStyle, textAlign: align as 'left' | 'center',
                      color: tableSort.key === key ? T.navy : T.textLight,
                      borderBottom: `2px solid ${tableSort.key === key ? T.gold : T.border}`,
                      cursor: 'pointer',
                    }}>
                      {label} {tableSort.key === key ? (tableSort.dir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedCountries.map(c => {
                  const riskLevel = getRiskLevel(c);
                  const riskStyle = getRiskLevelStyle(riskLevel);
                  return (
                    <tr
                      key={c.country}
                      style={{ transition: 'background 0.15s', cursor: 'pointer' }}
                      onClick={() => handleCountryClick(c.country)}
                      onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                    >
                      <td style={{
                        ...tdStyle, fontWeight: 700, color: T.navy,
                        textDecoration: 'underline', textDecorationColor: `${T.navy}40`,
                      }}>{c.country}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{ fontWeight: 700, color: T.navy, fontSize: 15 }}>{c.product_count.toLocaleString()}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {isUnknownCountry(c.country) ? (
                          <span style={{ color: T.textLight, fontSize: 12, fontStyle: 'italic' }}>Unknown</span>
                        ) : c.high_confidence_pct != null ? (
                          <span style={{
                            fontWeight: 700, fontSize: 14,
                            color: c.high_confidence_pct > 75 ? T.success : c.high_confidence_pct > 50 ? T.warning : T.danger,
                          }}>{c.high_confidence_pct}%</span>
                        ) : (
                          <span style={{ color: T.textLight, fontSize: 12 }}>N/A</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          background: riskStyle.bg, color: riskStyle.color,
                        }}>{getRiskLevelLabel(riskLevel)}</span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {c.active_recall_count > 0 ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: T.dangerBg, color: T.danger,
                          }}>{c.active_recall_count}</span>
                        ) : (
                          <span style={{ color: T.textLight }}>0</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {c.shortage_count > 0 ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: T.warningBg, color: T.warning,
                          }}>{c.shortage_count}</span>
                        ) : (
                          <span style={{ color: T.textLight }}>0</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
